import { useRef, useState } from 'react';
import type { ActionStep, Condition, ConditionOp, FieldDefinition } from '@dunea/plugin-sdk';
import {
  apiAutomationActions,
  apiAutomationTriggers,
  apiAutomationWebhookSecret,
  apiCreateAutomation,
  apiDeleteAutomation,
  apiListAutomations,
  apiTestAutomation,
  apiUpdateAutomation,
  type AutomationAction,
  type AutomationDraft,
  type AutomationTrigger,
  type TestRunResult,
} from '~/lib/api/automations';
import { classNames } from '~/utils/classNames';
import { FieldsForm } from '~/components/workbench/fields';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CAPTION,
  CARD,
  CopyField,
  ErrorBanner,
  Field,
  LoadError,
  Section,
  Select,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  OP_LABELS,
  apiErrorMessage,
  cleanInput,
  conditionValueText,
  draftFrom,
  draftProblems,
  emptyDraft,
  fieldFor,
  groupTriggers,
  insertPlaceholder,
  isTemplated,
  keyFromFieldId,
  moveItem,
  newCondition,
  newStep,
  opNeedsValue,
  opsFor,
  parseConditionValue,
  placeholdersFor,
  removeAt,
  stepErrorsFromApi,
  textFieldKeys,
} from './automations-model';

/** Builder for a new (`automationId` null) or existing automation. */
export function AutomationBuilder({
  appId,
  automationId,
  onBack,
}: {
  appId: string;
  automationId: string | null;
  onBack: () => void;
}) {
  const triggers = useResource(() => apiAutomationTriggers(appId), `${appId}:triggers`);
  const existing = useResource(
    automationId ? async () => (await apiListAutomations(appId)).find((a) => a.id === automationId) ?? null : null,
    `${appId}:${automationId ?? 'new'}`,
  );

  const header = (
    <button onClick={onBack} className={classNames(BTN_GHOST, '-ml-2.5')}>
      <div className="i-ph:arrow-left text-[13px]" />
      All automations
    </button>
  );

  if ((triggers.loading && !triggers.data) || (automationId && existing.loading && !existing.data)) {
    return (
      <div className="space-y-4">
        {header}
        <SkeletonCard lines={4} />
      </div>
    );
  }

  if (triggers.error || existing.error || (automationId && existing.data === null && !existing.loading)) {
    return (
      <div className="space-y-4">
        {header}
        <LoadError
          message={
            automationId && !existing.error && !triggers.error
              ? 'This automation no longer exists.'
              : 'Could not load the builder.'
          }
          onRetry={() => {
            void triggers.reload();
            void existing.reload();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {header}
      <BuilderForm
        appId={appId}
        automationId={automationId}
        triggers={triggers.data ?? []}
        initial={existing.data ? draftFrom(existing.data) : emptyDraft()}
        onDone={onBack}
      />
    </div>
  );
}

function BuilderForm({
  appId,
  automationId,
  triggers,
  initial,
  onDone,
}: {
  appId: string;
  automationId: string | null;
  triggers: AutomationTrigger[];
  initial: AutomationDraft;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState<AutomationDraft>(initial);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState<'save' | 'delete' | 'test' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [test, setTest] = useState<TestRunResult | null>(null);
  const actions = useResource(
    draft.trigger ? () => apiAutomationActions(appId, draft.trigger) : null,
    `${appId}:actions:${draft.trigger}`,
  );
  const trigger = triggers.find((t) => t.id === draft.trigger);
  const catalog = actions.data ?? [];
  const problems = draftProblems(draft, triggers, catalog);
  const shown = attempted ? { ...problems, ...stepErrors } : stepErrors;
  const usesWebhook = draft.actions.some((s) => s.action === 'webhook.post');

  const patch = (next: Partial<AutomationDraft>) => {
    setDraft((d) => ({ ...d, ...next }));
    setTest(null);
    setStepErrors({});
  };

  const setTrigger = (id: string) => {
    // Conditions are written against the trigger's fields: start them over.
    patch({ trigger: id, conditions: { all: [], any: [] } });
  };

  const payload = (): AutomationDraft => ({
    ...draft,
    name: draft.name.trim(),
    actions: draft.actions.map((s) => ({ ...s, input: cleanInput(s.input ?? {}) })),
  });

  const save = async () => {
    setAttempted(true);

    if (Object.keys(problems).length) {
      return;
    }

    setBusy('save');
    setError(null);

    try {
      if (automationId) {
        await apiUpdateAutomation(appId, automationId, payload());
      } else {
        await apiCreateAutomation(appId, payload());
      }

      onDone();
    } catch (err) {
      setStepErrors(stepErrorsFromApi(err));
      setError(apiErrorMessage(err, 'Could not save the automation.'));
    } finally {
      setBusy(null);
    }
  };

  const runTest = async () => {
    setAttempted(true);

    if (Object.keys(problems).length) {
      return;
    }

    setBusy('test');
    setError(null);

    try {
      setTest(await apiTestAutomation(appId, payload()));
    } catch (err) {
      setStepErrors(stepErrorsFromApi(err));
      setError(apiErrorMessage(err, 'Could not test the automation.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!automationId || !window.confirm(`Delete “${draft.name || 'this automation'}” and its run log?`)) {
      return;
    }

    setBusy('delete');
    setError(null);

    try {
      await apiDeleteAutomation(appId, automationId);
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete the automation.'));
      setBusy(null);
    }
  };

  return (
    <>
      <div className={classNames(CARD, 'space-y-4 p-4')}>
        <Field label="Name">
          <input
            className="ui-input"
            value={draft.name}
            maxLength={80}
            placeholder="Big order alert"
            onChange={(e) => patch({ name: e.target.value })}
          />
        </Field>
        {shown.name && <p className="-mt-2 text-[11px] text-danger">{shown.name}</p>}
        <Switch checked={draft.enabled} onChange={(enabled) => patch({ enabled })} label="Turned on" />
      </div>

      <Section title="When" icon="i-ph:lightning">
        <div className={classNames(CARD, 'space-y-2 p-4')}>
          <Select value={draft.trigger} onChange={setTrigger} label="Trigger">
            <option value="" disabled>
              Choose what starts it…
            </option>
            {groupTriggers(triggers).map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.triggers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          {trigger && <p className="text-[11px] leading-relaxed text-muted">{trigger.description}</p>}
          {shown.trigger && <p className="text-[11px] text-danger">{shown.trigger}</p>}
        </div>
      </Section>

      {trigger && (
        <Section title="Only if" icon="i-ph:funnel-simple">
          <ConditionGroup
            title="All of these are true"
            trigger={trigger}
            conditions={draft.conditions.all}
            onChange={(all) => patch({ conditions: { ...draft.conditions, all } })}
          />
          <ConditionGroup
            title="And at least one of these"
            trigger={trigger}
            conditions={draft.conditions.any}
            onChange={(any) => patch({ conditions: { ...draft.conditions, any } })}
            optional
          />
        </Section>
      )}

      {trigger && (
        <Section title="Then" icon="i-ph:list-numbers">
          {actions.error ? (
            <LoadError message="Could not load actions." onRetry={() => void actions.reload()} />
          ) : (
            <>
              {draft.actions.map((step, i) => (
                <StepCard
                  key={i}
                  index={i}
                  count={draft.actions.length}
                  step={step}
                  steps={draft.actions}
                  trigger={trigger}
                  catalog={catalog}
                  error={shown[`actions.${i}`]}
                  onChange={(next) => patch({ actions: draft.actions.map((s, j) => (j === i ? next : s)) })}
                  onMove={(delta) => patch({ actions: moveItem(draft.actions, i, delta) })}
                  onRemove={() => patch({ actions: removeAt(draft.actions, i) })}
                />
              ))}
              {draft.actions.length < 10 && (
                <AddStep
                  catalog={catalog}
                  loading={actions.loading}
                  onAdd={(a) => patch({ actions: [...draft.actions, newStep(a)] })}
                />
              )}
              {shown.actions && <p className="text-[11px] text-danger">{shown.actions}</p>}
              {usesWebhook && <WebhookSecret appId={appId} />}
            </>
          )}
        </Section>
      )}

      {test && <TestResult result={test} catalog={catalog} />}
      {error && <ErrorBanner message={error} />}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-hairline pt-4">
        {automationId && (
          <button
            onClick={() => void remove()}
            disabled={busy !== null}
            className={classNames(BTN_GHOST, 'mr-auto text-danger')}
          >
            {busy === 'delete' ? <Spinner /> : <div className="i-ph:trash text-[13px]" />}
            Delete
          </button>
        )}
        <button onClick={() => void runTest()} disabled={busy !== null || !trigger} className={BTN_SECONDARY}>
          {busy === 'test' ? <Spinner /> : <div className="i-ph:flask text-[13px]" />}
          Test with sample data
        </button>
        <button onClick={() => void save()} disabled={busy !== null} className="ui-btn-accent">
          {busy === 'save' && <Spinner />}
          {automationId ? 'Save changes' : 'Create automation'}
        </button>
      </div>
    </>
  );
}

// ─── conditions ───

function ConditionGroup({
  title,
  trigger,
  conditions,
  onChange,
  optional,
}: {
  title: string;
  trigger: AutomationTrigger;
  conditions: Condition[];
  onChange: (next: Condition[]) => void;
  optional?: boolean;
}) {
  if (optional && !conditions.length) {
    return (
      <button onClick={() => onChange([newCondition(trigger)])} className={classNames(BTN_GHOST, '-ml-2.5')}>
        <div className="i-ph:plus text-[13px]" />
        Add an “any of” group
      </button>
    );
  }

  return (
    <div className={classNames(CARD, 'space-y-2.5 p-4')}>
      <div className={CAPTION}>{title}</div>
      {!conditions.length && <p className="text-[11px] text-muted">No conditions: it runs every time.</p>}
      {conditions.map((c, i) => (
        <ConditionRow
          key={i}
          trigger={trigger}
          condition={c}
          onChange={(next) => onChange(conditions.map((x, j) => (j === i ? next : x)))}
          onRemove={() => onChange(removeAt(conditions, i))}
        />
      ))}
      {conditions.length < 20 && (
        <button
          onClick={() => onChange([...conditions, newCondition(trigger)])}
          className={classNames(BTN_GHOST, '-ml-2.5')}
        >
          <div className="i-ph:plus text-[13px]" />
          Add condition
        </button>
      )}
    </div>
  );
}

function ConditionRow({
  trigger,
  condition,
  onChange,
  onRemove,
}: {
  trigger: AutomationTrigger;
  condition: Condition;
  onChange: (next: Condition) => void;
  onRemove: () => void;
}) {
  const field = fieldFor(trigger, condition.path);
  const ops = opsFor(field?.type);
  // Text being typed is kept as-is; the parsed value is what's saved.
  const [text, setText] = useState(conditionValueText(condition.value));

  const setPath = (path: string) => {
    const f = fieldFor(trigger, path);
    const op = opsFor(f?.type).includes(condition.op) ? condition.op : opsFor(f?.type)[0];
    setText('');
    onChange({ path, op, value: f?.type === 'boolean' ? true : undefined });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select value={condition.path} onChange={setPath} label="Field" className="min-w-[140px] flex-1">
        {!field && <option value={condition.path}>{condition.path}</option>}
        {trigger.fields.map((f) => (
          <option key={f.path} value={f.path}>
            {f.label}
          </option>
        ))}
      </Select>
      <Select
        value={condition.op}
        onChange={(op) =>
          onChange({
            ...condition,
            op: op as ConditionOp,
            value: parseConditionValue(field?.type, op as ConditionOp, text),
          })
        }
        label="Comparison"
        className="w-[130px]"
      >
        {ops.map((op) => (
          <option key={op} value={op}>
            {OP_LABELS[op]}
          </option>
        ))}
      </Select>
      {opNeedsValue(condition.op) &&
        (field?.type === 'boolean' ? (
          <Select
            value={String(condition.value === true)}
            onChange={(v) => onChange({ ...condition, value: v === 'true' })}
            label="Value"
            className="w-[90px]"
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        ) : field?.type === 'select' && condition.op !== 'in' ? (
          <Select
            value={String(condition.value ?? '')}
            onChange={(v) => onChange({ ...condition, value: v })}
            label="Value"
            className="w-[120px]"
          >
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        ) : (
          <input
            className="ui-input !w-[130px] !py-2"
            aria-label="Value"
            value={text}
            inputMode={field?.type === 'integer' || field?.type === 'decimal' ? 'decimal' : undefined}
            placeholder={condition.op === 'in' ? 'a, b, c' : 'Value'}
            onChange={(e) => {
              setText(e.target.value);
              onChange({ ...condition, value: parseConditionValue(field?.type, condition.op, e.target.value) });
            }}
          />
        ))}
      <button onClick={onRemove} className="ui-btn-icon" title="Remove condition" aria-label="Remove condition">
        <div className="i-ph:x text-[13px] text-muted" />
      </button>
    </div>
  );
}

// ─── steps ───

function StepCard({
  index,
  count,
  step,
  steps,
  trigger,
  catalog,
  error,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  step: ActionStep;
  steps: ActionStep[];
  trigger: AutomationTrigger;
  catalog: AutomationAction[];
  error?: string;
  onChange: (next: ActionStep) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const def = catalog.find((a) => a.id === step.action);
  const defs: FieldDefinition[] = def?.input ?? [];
  const input = step.input ?? {};
  // A templated value in a typed field (e.g. "{{order.total}}" for a number) is edited as text.
  const display = defs.map((d) =>
    isTemplated(input[d.key]) && d.type !== 'select' ? { ...d, type: 'single_line_text' as const, list: false } : d,
  );
  const textKeys = textFieldKeys(display);
  const lastFocused = useRef<string | null>(null);
  const placeholders = placeholdersFor(trigger, steps, index, catalog);

  const insert = (token: string) => {
    const key = lastFocused.current ?? textKeys[textKeys.length - 1];

    if (key) {
      onChange({ ...step, input: { ...input, [key]: insertPlaceholder(input[key], token) } });
    }
  };

  return (
    <div className={classNames(CARD, 'space-y-3 p-4')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={CAPTION}>Step {index + 1}</div>
          <div className="mt-1 text-[13px] font-medium text-primary">{def?.title ?? step.action}</div>
          {def && <p className="mt-0.5 text-[11px] leading-relaxed text-muted">{def.description}</p>}
          {def?.plugin && <p className="mt-0.5 text-[11px] text-faint">From {def.plugin.name}</p>}
        </div>
        <div className="flex shrink-0 items-center">
          <button
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="ui-btn-icon disabled:opacity-30"
            title="Move up"
            aria-label="Move step up"
          >
            <div className="i-ph:arrow-up text-[13px] text-muted" />
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={index === count - 1}
            className="ui-btn-icon disabled:opacity-30"
            title="Move down"
            aria-label="Move step down"
          >
            <div className="i-ph:arrow-down text-[13px] text-muted" />
          </button>
          <button onClick={onRemove} className="ui-btn-icon" title="Remove step" aria-label="Remove step">
            <div className="i-ph:trash text-[13px] text-muted" />
          </button>
        </div>
      </div>

      {display.length > 0 && (
        <div
          onFocusCapture={(e) => {
            const key = keyFromFieldId((e.target as HTMLElement).id, textKeys);

            if (key) {
              lastFocused.current = key;
            }
          }}
        >
          <FieldsForm fields={display} values={input} onChange={(next) => onChange({ ...step, input: next })} />
        </div>
      )}

      {textKeys.length > 0 && placeholders.length > 0 && (
        <PlaceholderMenu placeholders={placeholders} onInsert={insert} />
      )}

      <Switch
        checked={Boolean(step.continueOnError)}
        onChange={(v) => onChange({ ...step, continueOnError: v || undefined })}
        label="If this step fails, keep going"
      />
      {error && <p className="text-[11px] text-danger">{error}</p>}
    </div>
  );
}

function PlaceholderMenu({
  placeholders,
  onInsert,
}: {
  placeholders: ReturnType<typeof placeholdersFor>;
  onInsert: (token: string) => void;
}) {
  const groups = [...new Set(placeholders.map((p) => p.group))];

  return (
    <Select value="" onChange={(token) => token && onInsert(token)} label="Insert a field from the event">
      <option value="">Insert a field from the event…</option>
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {placeholders
            .filter((p) => p.group === g)
            .map((p) => (
              <option key={p.token} value={p.token}>
                {p.label} — {p.token}
              </option>
            ))}
        </optgroup>
      ))}
    </Select>
  );
}

function AddStep({
  catalog,
  loading,
  onAdd,
}: {
  catalog: AutomationAction[];
  loading: boolean;
  onAdd: (a: AutomationAction) => void;
}) {
  const builtins = catalog.filter((a) => !a.plugin);
  const plugins = catalog.filter((a) => a.plugin);

  return (
    <div className="flex items-center gap-2">
      <Select
        value=""
        onChange={(id) => {
          const a = catalog.find((x) => x.id === id);

          if (a) {
            onAdd(a);
          }
        }}
        label="Add a step"
        disabled={loading}
        className="flex-1"
      >
        <option value="">{loading ? 'Loading actions…' : 'Add a step…'}</option>
        <optgroup label="Built in">
          {builtins.map((a) => (
            <option key={a.id} value={a.id} disabled={Boolean(a.unavailable)}>
              {a.title}
              {a.unavailable ? ' (not for this trigger)' : ''}
            </option>
          ))}
        </optgroup>
        {plugins.length > 0 && (
          <optgroup label="From your plugins">
            {plugins.map((a) => (
              <option key={a.id} value={a.id} disabled={Boolean(a.unavailable)}>
                {a.plugin!.name}: {a.title}
              </option>
            ))}
          </optgroup>
        )}
      </Select>
    </div>
  );
}

function WebhookSecret({ appId }: { appId: string }) {
  const secret = useResource(() => apiAutomationWebhookSecret(appId), `${appId}:webhook-secret`);
  const [shown, setShown] = useState(false);

  if (!secret.data?.secret) {
    return null;
  }

  return (
    <div className={classNames(CARD, 'space-y-2 p-4')}>
      <div className={CAPTION}>Verify webhook requests</div>
      <p className="text-[11px] leading-relaxed text-muted">
        Each POST carries a <code className="font-geist-mono">{secret.data.header}</code> header: t=&lt;unix
        time&gt;,v1=&lt;HMAC-SHA256 of “t.body” with this secret&gt;.
      </p>
      {shown ? (
        <CopyField value={secret.data.secret} label="Signing secret" />
      ) : (
        <button onClick={() => setShown(true)} className={BTN_SECONDARY}>
          <div className="i-ph:key text-[13px]" />
          Show signing secret
        </button>
      )}
    </div>
  );
}

function TestResult({ result, catalog }: { result: TestRunResult; catalog: AutomationAction[] }) {
  return (
    <div className={classNames(CARD, 'space-y-3 p-4')} aria-live="polite">
      <div className="flex items-center gap-2">
        <div
          className={classNames(
            result.conditionsPass ? 'i-ph:check-circle text-success' : 'i-ph:minus-circle text-muted',
            'text-[15px]',
          )}
        />
        <span className="text-[12px] text-primary">
          {result.conditionsPass
            ? 'With the sample event, the conditions pass and these steps would run:'
            : 'With the sample event, the conditions do not pass: nothing would run.'}
        </span>
      </div>
      <p className="text-[11px] text-muted">A test sends nothing and changes nothing.</p>
      {result.steps.map((s, i) => (
        <div key={i} className="space-y-1 rounded-lg border border-hairline bg-sunken p-3">
          <div className="text-[11px] font-medium text-secondary">
            {i + 1}. {catalog.find((a) => a.id === s.action)?.title ?? s.action}
          </div>
          {Object.entries(s.input).map(([k, v]) => (
            <div key={k} className="text-[11px] leading-relaxed text-muted">
              <span className="text-faint">{k}: </span>
              <span className="whitespace-pre-wrap break-words text-secondary">
                {typeof v === 'string' ? v : JSON.stringify(v)}
              </span>
            </div>
          ))}
          {s.problem && <p className="text-[11px] text-warning">{s.problem}</p>}
        </div>
      ))}
    </div>
  );
}
